// src/components/manuscript/structureTokens.js
//
// Design tokens for the Manuscript Structure page.
//
// The prototype this page was built from carried its own palette — a purple of
// #4B2E92 and a gold of #C9A24B. Those are **not** the app's colours. `purple`
// and `gold` below are `theme.palette.primary.main` and
// `theme.palette.secondary.main`, and `goldLight` / `goldDeep` are the two stops
// either side of that gold in the `containedSecondary` gradient in `theme.js`.
// Everything else is a tint or shade of those two, so the page reads as part of
// the app rather than as an import.
//
// Kept as plain values rather than reached for through `useTheme()` because most
// of them are shades the MUI palette does not carry, and mixing two sources for
// one page's colours is how a palette drifts.
//
// Fonts are not defined here: Source Serif 4, IBM Plex Mono and Inter are already
// loaded in `public/index.html`, so the stacks below just name them.

export const SP = {
  purple: '#413B8D',      // theme.palette.primary.main
  purpleDeep: '#231F4D',  // the dark ground behind the element list
  purpleMid: '#5A52B0',
  purpleSoft: '#8F89C4',
  lavender: '#F4F2FB',
  lineSoft: '#E3E0F2',

  gold: '#D4AF37',        // theme.palette.secondary.main
  goldLight: '#F5D76E',   // containedSecondary gradient, light stop
  goldDeep: '#C49A00',    // containedSecondary gradient, deep stop

  black: '#14121B',
  gray: '#6B6A78',
  white: '#FFFFFF',
  lineLt: 'rgba(255,255,255,0.14)',
};

// One colour per category, used by the position strip and nothing else. They
// have to stay distinguishable from each other at 8px tall, which is why body
// matter takes the gold rather than a third purple.
export const CATEGORY_COLOR = {
  front: SP.purpleMid,
  body: SP.goldDeep,
  back: '#332C6B',
};

export const FONT = {
  serif: '"Source Serif 4", Georgia, serif',
  mono: '"IBM Plex Mono", ui-monospace, monospace',
  sans: '"Inter", -apple-system, sans-serif',
};

// The small uppercase monospace label used for field labels and eyebrows.
export const eyebrowSx = {
  fontFamily: FONT.mono,
  fontSize: 10.5,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: SP.goldDeep,
};

// The app's responsive tiers, as `@media` strings for `sx`.
//
// 900 and 640, not MUI's `md`/`sm`, because the Manuscript Editor already draws
// its lines there (`src/styles/EditorPage.css`) and a second set of breakpoints
// in the same product means the sidebar, the board and this page rearrange at
// different window widths. The rule the editor establishes at 900 is the
// important part: **panels float over the work surface, they never stack below
// it.** A panel pushed under a full-height column is a panel nobody can see.
//
// These lived in corkTokens.js first, which already re-exported FONT from here;
// they moved down to the shared file when the Structure page needed them too,
// so a page does not have to import the Cork Board's tokens to know how wide a
// phone is. corkTokens re-exports them, so the Cork Board is unchanged.
export const BP = {
  panelFloats: '@media (max-width: 900px)',
  docked: '@media (min-width: 901px)',
  phone: '@media (max-width: 640px)',
};

// A floating panel's width, matching `.editor-right-rail` at the same
// breakpoint. 85vw rather than a fixed width so it never covers the whole
// surface — you have to be able to see what you are editing.
export const FLOATING_PANEL_W = 'min(340px, 85vw)';
